/**
 * The 401 error view — registered as `config.views.errorViews[401]`.
 *
 * WHY THIS EXISTS
 *
 * Stock Volto answers an anonymous request for a private object with a
 * `<Redirect>` to `<path>/login?return_url=…`. Because `server.jsx` tests
 * `context.url` BEFORE `context.error_code`, that redirect always wins and the
 * response status is **302** — and the login page it lands on answers 200 with
 * no robots meta. To a crawler that is not "this is gone", it is "this moved,
 * permanently keep asking". So Google kept the URLs and kept serving them.
 *
 * Measured before this change: 494 objects were retracted in one automated
 * action on 2026-08-05 after 23 days public, sitemapped and nav-linked, and
 * **36 of 408 Google organic sessions (8.8%) then landed on a login wall** —
 * one such URL was the 7th-largest landing page on the property, bounce 0.83.
 * Googlebot took 237 of those redirects and fetched login pages 187 times in
 * 12 days. A 302 is the one status that guarantees this never self-heals.
 *
 * So: for an ANONYMOUS visitor we answer **410 Gone** with a real page. 410 is
 * the correct code — the content existed at this URL and deliberately no longer
 * does — and it is the strongest de-indexing signal available short of a
 * removal request. For a SIGNED-IN user we keep core's 401 behaviour and its
 * message, because for them it genuinely is a permission problem.
 *
 * THE DELIBERATE TRADEOFF
 *
 * Volto cannot tell "this content is private" from "you need to sign in" —
 * the backend answers 401 to both. After this change a signed-out editor who
 * opens a private URL directly gets this page with a sign-in link, instead of
 * being bounced into a login form automatically. That is the right trade: the
 * anonymous public is thousands of visitors a month, the editors are a handful
 * and the sign-in link is one click. See BL-0041.
 *
 * This is registered as an error VIEW rather than shadowing core's
 * `theme/Unauthorized/Unauthorized`, deliberately: the CMS-UI imports that
 * component directly in six places (Contents, Edit, History, Diff, LinksToItem,
 * UserGroupMembership) where the auto-redirect to login is the desired
 * behaviour. Registering the view changes only the public content route.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container } from 'semantic-ui-react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import BodyClass from '@plone/volto/helpers/BodyClass/BodyClass';

import useSiteConfig from '../../helpers/useSiteConfig';
import PageHeader from '../Layout/PageHeader';

/**
 * Propagate an HTTP status back to the SSR process.
 *
 * This is core's `withServerErrorCode` HOC (helpers/Utils/Utils) inlined,
 * because the HOC bakes ONE code in at module scope and we need the code to
 * depend on whether the visitor is signed in. The empty-keys guard is core's
 * and is load-bearing: it makes the first writer win, so an inner component
 * cannot overwrite a status an outer one already set.
 */
const setServerStatus = (props, code) => {
  const { staticContext } = props;
  if (staticContext && Object.keys(staticContext).length === 0) {
    staticContext.error_code = code;
    staticContext.error = props.error;
  }
};

const UnauthorizedView = (props) => {
  const token = useSelector((state) => state.userSession.token);
  const errorMessage = useSelector((state) => state.apierror?.message);
  const location = useLocation();
  const site = useSiteConfig();

  // Signed in, but not permitted: genuinely 401. Core's copy, core's status.
  if (token) {
    setServerStatus(props, 401);
    return (
      <Container className="view-wrapper">
        <BodyClass className="view-unauthorized" />
        <Helmet>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <PageHeader
          title="Unauthorized"
          lede="You are signed in, but this item is not available to your account."
        />
        {errorMessage ? <p className="description">{errorMessage}</p> : null}
        <p>
          If you believe you should have access to this item, ask a site
          administrator to check its sharing settings.
        </p>
      </Container>
    );
  }

  // Anonymous: 410 Gone. This is the whole point of the component.
  setServerStatus(props, 410);

  const path = location.pathname.replace(/\/$/, '');
  const loginUrl = `${path}/login?return_url=${encodeURIComponent(
    location.pathname,
  )}`;

  return (
    <Container className="view-wrapper">
      <BodyClass className="view-gone" />
      <Helmet>
        <title>{`Not available | ${site.seoName}`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <PageHeader
        title="This page is not publicly available"
        lede={
          `The item that was at this address is no longer published in ` +
          `${site.title}. It may have been withdrawn, superseded, or kept for ` +
          `internal use.`
        }
      />
      <p>
        Nothing here is lost — the archive holds the full published record, and
        the document you were looking for is very likely still in it under its
        own number.
      </p>
      <ul>
        <li>
          <Link to="/documents">Browse all documents</Link> — filter by type,
          series, status and date.
        </li>
        <li>
          <Link to="/search">Search the archive</Link> — searches inside the
          documents themselves, not just their titles. If you arrived here
          looking for a specific document, searching its number is the fastest
          route.
        </li>
        <li>
          <Link to="/">{site.title} home</Link>
        </li>
      </ul>
      <p className="pp-gone__editor-note">
        If you maintain this archive, this item may simply be unpublished:{' '}
        <a href={loginUrl}>sign in</a> to view it.
      </p>
    </Container>
  );
};

UnauthorizedView.displayName = 'unauthorized';

export default UnauthorizedView;
